<template>
  <span class="sp-responsive-image">
    <picture v-if="shouldRender">
      <source v-if="srcSmall" :srcset="srcSmall" :media="mediaSmall" />
      <source v-if="srcMedium" :srcset="srcMedium" :media="mediaMedium" />
      <source v-if="srcLarge" :srcset="srcLarge" :media="mediaLarge" />
      <img :src="src" :alt="alt" :loading="loading" :fetchpriority="fetchpriority" />
    </picture>
  </span>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean } from "#ce/utils/props";
import { props } from "./props";

const componentProps = defineProps(props);
const { smBreakpoint, lgBreakpoint } = useBreakpoints();

const { exposeMethods } = useExpose();

const isDeferred = computed(() => toBoolean(componentProps.defer));
const isLoaded = ref(!isDeferred.value);

const mediaSmall = computed(() => `(max-width: ${smBreakpoint.value})`);
const mediaMedium = computed(() => `(max-width: ${lgBreakpoint.value})`);
const mediaLarge = computed(() => `(min-width: ${lgBreakpoint.value})`);

const shouldRender = computed(() => {
  if (!componentProps.src) {
    return false;
  }
  return isDeferred.value ? isLoaded.value : true;
});

watch(isDeferred, (deferred) => {
  if (!deferred) {
    isLoaded.value = true;
  }
});

function load() {
  if (isLoaded.value) {
    return;
  }
  isLoaded.value = true;
}

exposeMethods({ load });
</script>

<style lang="scss">
:host {
  display: block;

  // Honour both the legacy --sp-responsive-image-* tokens (still in the wild
  // outside this repo) and the documented public --sp-comp-responsive-image-*
  // tokens — the legacy form wins when both are set, matching the original
  // chain so existing consumer CSS doesn't silently regress.
  --responsive-image-border-radius: var(
    --sp-responsive-image-border-radius,
    var(--sp-comp-responsive-image-border-radius, 0)
  );
  --responsive-image-width: var(--sp-responsive-image-width, var(--sp-comp-responsive-image-width, auto));
  --responsive-image-height: var(--sp-responsive-image-height, var(--sp-comp-responsive-image-height, auto));
}
</style>

<style scoped lang="scss">
.sp-responsive-image {
  display: block;
  width: var(--responsive-image-width);
  height: var(--responsive-image-height);

  picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: var(--responsive-image-border-radius);
    object-fit: cover;
  }
}
</style>
